import { Box, Stack, Text, ThemeIcon, UnstyledButton } from '@mantine/core';
import { IconCloudUpload } from '@tabler/icons-react';
import { useRef, useState } from 'react';

import { BulkUploadType, MEDIA_CONFIG } from '../../constants/mediaConfig';
import { formatMaxMediaSizeLabel } from '../../constants/uploadLimits';

import { Platform } from '@/features/pages';
import { COLORS } from '@/utils/colors';

type Props = {
  mediaType: BulkUploadType;
  platform: Platform;
  compact?: boolean;
  isDisabled?: boolean;
  onFiles: (files: File[]) => void;
};

export const DropZone = ({ mediaType, platform, compact, isDisabled, onFiles }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const config = MEDIA_CONFIG[mediaType];

  const handleFiles = (fileList: FileList | null) => {
    if (isDisabled || !fileList?.length) return;
    onFiles(Array.from(fileList));
  };

  const openPicker = () => {
    if (isDisabled) return;
    inputRef.current?.click();
  };

  return (
    <UnstyledButton
      w="100%"
      disabled={isDisabled}
      onClick={openPicker}
      onDragEnter={(event) => {
        if (isDisabled) return;
        event.preventDefault();
        setIsDragging(true);
      }}
      onDragOver={(event) => {
        if (isDisabled) return;
        event.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={(event) => {
        event.preventDefault();
        setIsDragging(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        setIsDragging(false);
        handleFiles(event.dataTransfer.files);
      }}
      style={{
        display: 'block',
        opacity: isDisabled ? 0.55 : 1,
        cursor: isDisabled ? 'not-allowed' : undefined,
      }}
    >
      <Box
        p={compact ? 'lg' : 'xl'}
        style={{
          borderRadius: 12,
          border: `1px dashed ${COLORS.primary}`,
          backgroundColor: isDragging ? 'rgba(68, 133, 207, 0.08)' : 'var(--mantine-color-dark-7)',
          transition: 'background-color 150ms ease',
        }}
      >
        <Stack align="center" gap="sm">
          <ThemeIcon variant="light" radius="md" size={compact ? 40 : 48} color="blue">
            <IconCloudUpload size={compact ? 22 : 28} stroke={1.5} />
          </ThemeIcon>
          <Text size="sm" fw={600}>
            {compact ? 'Добавить ещё файлы' : 'Перетащите файлы сюда или выберите с диска'}
          </Text>
          <Text size="xs" c="dimmed">
            {config.formatsHint} · {formatMaxMediaSizeLabel(platform, mediaType)}
          </Text>
        </Stack>
      </Box>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={config.accept}
        hidden
        onChange={(event) => {
          handleFiles(event.target.files);
          event.target.value = '';
        }}
      />
    </UnstyledButton>
  );
};
